import React from 'react';
import dayjs from 'dayjs';
import type { InspectionRecord } from '@/services/quality.service';
import type { ApprovalPrintSigner } from '@/services/approval.service';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';
import { PrintableApprovalSigners } from '@/components/approval/PrintableApprovalSigners';

export type ReceivingQcPrintCompanyInfo = {
  name: string;
  legalName?: string;
  address: string;
  logo?: string;
};

type SampleRow = {
  sample_number?: number;
  sample_qty?: number;
  status?: string;
  notes?: string | null;
  receiving_inspection_detail_id?: string | null;
};

type DetailRow = {
  id: string;
  item_code?: string | null;
  item_description?: string;
  po_line_number?: number;
  quantity_ordered?: number;
  quantity_received?: number;
  unit?: string;
  acceptance_status?: string;
  remarks?: string;
};

type HeatRow = {
  heat_number: string;
  dimension?: string;
  quantity?: number;
  item_code?: string | null;
  mtc_document_number?: string | null;
};

export type ReceivingInspectionPrintRecord = InspectionRecord & {
  lot_quantity?: number | null;
  sample_size?: number | null;
  sampling_plan?: string | null;
  pending_result?: string | null;
  dimensional_bypass_reason?: string | null;
  samples?: SampleRow[];
  receiving_details?: DetailRow[];
  heat_lines?: HeatRow[];
  dimensional_inspection?: {
    inspection_number?: string;
    status?: string;
    result?: string;
  } | null;
  grn?: {
    grn_number?: string;
    grn_status?: string;
    inspection_status?: string;
  };
};

function dash(v?: string | number | null): string {
  if (v == null || String(v).trim() === '') return '—';
  return String(v);
}

function formatDate(v?: string | null): string {
  if (!v) return '—';
  return dayjs(v).format('DD MMM YYYY');
}

export function ReceivingInspectionPrintView({
  record,
  companyInfo,
  approvalSigners = [],
}: {
  record: ReceivingInspectionPrintRecord;
  companyInfo: ReceivingQcPrintCompanyInfo;
  approvalSigners?: ApprovalPrintSigner[];
}) {
  const legal = companyInfo.legalName || companyInfo.name;
  const details = record.receiving_details || [];
  const samples = record.samples || [];
  const heats = record.heat_lines || [];
  const dim = record.dimensional_inspection;

  const metaLines = [
    { label: 'Inspection No', value: dash(record.inspection_number) },
    { label: 'Date', value: formatDate(record.actual_date || record.scheduled_date) },
    { label: 'Status', value: dash(record.status) },
    { label: 'Result', value: dash(record.result || record.pending_result) },
    { label: 'GRN', value: dash(record.grn?.grn_number) },
    { label: 'GRN QC status', value: dash(record.grn?.inspection_status) },
    { label: 'Lot qty', value: dash(record.lot_quantity) },
    { label: 'Samples taken', value: dash(record.sample_size) },
    { label: 'Sampling plan', value: dash(record.sampling_plan) },
    {
      label: 'Dimension insp.',
      value: dim
        ? `${dash(dim.inspection_number)} · ${dash(dim.status)} / ${dash(dim.result)}`
        : record.dimensional_bypass_reason
          ? `Bypassed — ${record.dimensional_bypass_reason}`
          : 'Not initiated',
    },
  ];

  const lineRows = details.map((d, i) => ({
    id: d.id || String(i),
    lineNumber: d.po_line_number ?? i + 1,
    description: dash(d.item_description),
    cells: {
      line: String(d.po_line_number ?? i + 1),
      item: dash(d.item_code),
      description: dash(d.item_description),
      ordered: dash(d.quantity_ordered),
      received: dash(d.quantity_received),
      uom: dash(d.unit),
      acceptance: dash(d.acceptance_status),
    },
  }));

  const sampleRows = samples.map((s, i) => ({
    id: `s-${i}`,
    cells: {
      no: String(s.sample_number ?? i + 1),
      qty: dash(s.sample_qty),
      status: dash(s.status),
      notes: dash(s.notes),
    },
  }));

  const heatRows = heats.map((h, i) => ({
    id: `h-${i}`,
    cells: {
      item: dash(h.item_code),
      heat: dash(h.heat_number),
      dim: dash(h.dimension),
      qty: dash(h.quantity),
      mtc: dash(h.mtc_document_number),
    },
  }));

  return (
    <EngineCommercialA4Print
      title="Receiving Inspection Note"
      documentNumber={record.inspection_number}
      partyName={legal}
      metaLines={metaLines}
      introText={
        record.findings || record.observations
          ? `Findings / observations: ${[record.findings, record.observations].filter(Boolean).join(' | ')}`
          : undefined
      }
      columns={[
        { key: 'line', label: 'Line', width: '48px' },
        { key: 'item', label: 'Item', width: '90px' },
        { key: 'description', label: 'Description', width: '160px' },
        { key: 'ordered', label: 'Ordered', width: '70px', align: 'right' },
        { key: 'received', label: 'Received', width: '70px', align: 'right' },
        { key: 'uom', label: 'UOM', width: '50px' },
        { key: 'acceptance', label: 'Acceptance', width: '90px' },
      ]}
      lines={lineRows}
      finals={[
        { id: 'samples', heightPx: samples.length ? 80 + samples.length * 22 : 40 },
        { id: 'heats', heightPx: heats.length ? 80 + heats.length * 22 : 40 },
        { id: 'signers', heightPx: 160 },
      ]}
      finalBlocks={{
        samples: (
          <div>
            <strong>Sampling</strong>
            {sampleRows.length ? (
              <table style={{ width: '100%', marginTop: 8, fontSize: 11, borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th align="left">#</th>
                    <th align="right">Qty</th>
                    <th align="left">Status</th>
                    <th align="left">Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {sampleRows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.cells.no}</td>
                      <td align="right">{r.cells.qty}</td>
                      <td>{r.cells.status}</td>
                      <td>{r.cells.notes}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ marginTop: 4 }}>—</div>
            )}
          </div>
        ),
        heats: (
          <div>
            <strong>Heat / MTC traceability</strong>
            {heatRows.length ? (
              <table style={{ width: '100%', marginTop: 8, fontSize: 11, borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th align="left">Item</th>
                    <th align="left">Heat</th>
                    <th align="left">Dimension</th>
                    <th align="right">Qty</th>
                    <th align="left">MTC</th>
                  </tr>
                </thead>
                <tbody>
                  {heatRows.map((r) => (
                    <tr key={r.id}>
                      <td>{r.cells.item}</td>
                      <td>{r.cells.heat}</td>
                      <td>{r.cells.dim}</td>
                      <td align="right">{r.cells.qty}</td>
                      <td>{r.cells.mtc}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div style={{ marginTop: 4 }}>—</div>
            )}
          </div>
        ),
        signers: <PrintableApprovalSigners signers={approvalSigners} />,
      }}
      letterhead
      docType="receiving_inspection"
    />
  );
}

export default ReceivingInspectionPrintView;
